import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiFetch } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    try {
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);
  const normalizeUser = (u) => {
    if (!u) return null;
    return {
      ...u,
      firstName: u.first_name || u.firstName,
      lastName: u.last_name || u.lastName,
      salonName: u.salon_name || u.salonName,
      salonAddress: u.salon_address || u.salonAddress,
      salonCity: u.salon_city || u.salonCity,
      createdAt: u.created_at || u.createdAt,
      updatedAt: u.updated_at || u.updatedAt
    };
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const u = await apiFetch('/auth/me');
        const normalized = normalizeUser(u);
        setUser(normalized);
        localStorage.setItem('user', JSON.stringify(normalized));
      } catch (err) {
        console.error("Sesja wygasła lub błąd autoryzacji:", err);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const data = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      
      const normalized = normalizeUser(data.user);
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(normalized));
      setUser(normalized);
      return { success: true, user: normalized };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const register = async (userData) => {
    try {
      const data = await apiFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      
      const normalized = normalizeUser(data.user);
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(normalized));
      setUser(normalized);
      return { success: true, user: normalized };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      await apiFetch('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken })
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/login';
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Ładowanie aplikacji...
        </div>
      )}
    </AuthContext.Provider>
  );
};